"use client";

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftLong,
  faArrowRightLong,
} from "@fortawesome/free-solid-svg-icons";

import styles from "../../styles/patient-page-modules/PatientDetail.module.css";
import GeneralDetails from "../../components/patient-detail/generalDetails";
import Summary from "../../components/patient-detail/summary";
import Contact from "../../components/patient-detail/contact";
import Charts from "../../components/patient-detail/charts";
import Chatbot from "../../components/patient-detail/chatbot";
import ChatbotV2 from "../../components/shared-modules/chatbot";
import Monitoring from "../../components/patient-detail/monitoring";
import Notes from "../../components/patient-detail/notes";
import MonitoringTime from "./monitoringTime";
import PatientSettings from "./patientSettings";
import BillingProgressGroup from "../../components/patient-detail/billing-indicator/BillingProgressGroup";
import PatientDocuments from "./patientDocuments";
import Spinner from "../../components/spinner";
import PatientDevices from "./patientDevices";
import CollapsableSection from "../../components/collapsableSection";
import { getUserIdentity } from "../../utils/authorization";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPatientGeneralDetails,
  createMonitoringSession,
} from "../../services/patient/details";
import { getPatientLocationId, setPatientLocationId } from "../../utils/helper";
import Reload from "../../components/ui/reload-data";
import { formatLastCalledHeader } from "../../utils/dateFormatters";
import TimeTracker from "../../components/shared-modules/time-tracker";
import Button from "../../components/button-v1";
import AddNoteDialogue from "../../components/patient-detail/notes/add-note-dialogue";
import toast from "react-hot-toast";
import {
  getPatientContacts,
  getPatientNotes,
} from "../../services/patient/settings";
import { getCarePlan } from "../../services/patient/carePlan";
import axios from "axios";
import { BASE_URL_ML } from "../../utils/constants";
import { handleApiError } from "../../utils/handleApiError";
import { streamingFetch, createStreamController } from "../../utils/streamingFetch";
import {
  Tabs,
  Panel,
  TabList,
  TabPanels,
  TabTrigger,
} from "../../components/ui/tab";
import { useToggle } from "../../hooks/useToggle";
import useRoles from "../../hooks/useRoles";
import { MdHeight } from "react-icons/md";
import { FaExpand } from "react-icons/fa";
import InstructionalVideos from "./InstructionalVideos";
import DetailsTab from "../../components/patient-detail/DetailsTab";
import { FaHistory } from "react-icons/fa";

const PatientDetail = () => {
  const { position } = useRoles();
  const {
    isCareplanExpand,
    setIsCareplanExpand,
    isCurrentMonthCareplanExists,
    setIsCurrentMonthCareplanExists,
    careplanMode,
    setCareplanMode,
  } = useToggle();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accountId = sessionStorage.getItem("acc_id");
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const schema_name = sessionStorage.getItem("schema_name");
  const [queryParams, setQueryParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem("patient_detail_tab") || "details";
  });
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);
  // Read the URL first, not sessionStorage - sessionStorage still holds the
  // *previous* patient's id at the instant this component (re)mounts (it's
  // only updated by the effect below, which runs one tick later). Since
  // App.js now remounts this page fresh per patient (key={location.search}),
  // the URL is always correct immediately; falling back to sessionStorage
  // only covers the rare case patient_id isn't in the URL at all.
  const [patientId, setPatientId] = useState(
    () => queryParams.get("patient_id") || sessionStorage.getItem("_pt_id")
  );

  const [noteToPaste, setNoteToPaste] = useState(null);

  const [elapsedTimeCCM, setElapsedTimeCCM] = useState(0);
  const [elapsedTimeRPM, setElapsedTimeRPM] = useState(0);
  const [isTrackingCCM, setIsTrackingCCM] = useState(true);
  const [isTrackingRPM, setIsTrackingRPM] = useState(true);

  const [patient_id] = useState(() => sessionStorage.getItem("_pt_id"));
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [monitoringSeconds, setMonitoringSeconds] = useState({
    seconds: 0,
    ccm_seconds: 0,
    rpm_seconds: 0,
  });

  const [isSectionOpen, setIsSectionOpen] = useState({
    section_1: true,
    section_2: true,
    section_3: true,
    section_4: true,
    section_5: true,
    section_6: true,
  });

  const [isCopilotExpand, setIsCopilotExpand] = useState(false);

  // Controls which inner tab is visible in the NotesAIPanel (right column).
  // Lifted out of NotesAIPanel so the CarePlan "Toca Copilot" action can
  // switch to the AI tab and start the CCM chat from outside the panel.
  const [notesAiTab, setNotesAiTab] = useState("notes");

  const [currentPatientIndex, setCurrentPatientIndex] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState("");

  const [chatBotType, setChatBotType] = useState("RPM");
  const [sessionId, setSessionId] = useState("");
  const [isChatStartedWithReport, setIsChatStartedWithReport] = useState(false);
  // file_ref returned by the AI upload-file endpoint; appended to the Start
  // Chat FormData only when a document has been uploaded before starting chat.
  const [fileRef, setFileRef] = useState(null);
  const [ccmSessionId, setCcmSessionId] = useState("");
 const [ccmUseGenericEndpoint, setCcmUseGenericEndpoint] = useState(
  () => JSON.parse(sessionStorage.getItem("plan_created"))
);
  useEffect(() => {
    setCcmUseGenericEndpoint(isCurrentMonthCareplanExists);
  }, [patientId, isCurrentMonthCareplanExists]);
  const [isCcmChatStartedWithReport, setIsCcmChatStartedWithReport] =
    useState(false);
  const [isChatStarted, setIsChatStarted] = useState(false);
  const [isCcmChatStarted, setIsCcmChatStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [ccmMessages, setCcmMessages] = useState([]);
  const [ccmMessage, setCcmMessage] = useState("");
  // Which CCM quick action is in flight ("condition-guidelines" | "warning-signs" | null)
  const [ccmQuickAction, setCcmQuickAction] = useState(null);
  const [drNoteReceived, setDrNoteReceived] = useState(false);
  const [drNote, setDrNote] = useState(false);
  const [patientType, setPatientType] = useState("");
  const { hasChatbotAccess } = getUserIdentity();

  // Fired by the CarePlan "Toca Copilot" dropdown action. Switches the
  // right-column panel to the AI tab, forces CCM scope, and starts the
  // CCM chat — the same effect as clicking the "Generate/Update Care Plan"
  // button inside <Chatbot />, but reachable even when the chatbot isn't
  // mounted yet (Notes tab is what shows by default).
  const handleStartTocaCopilot = useCallback(() => {
    setChatBotType("CCM");
    setNotesAiTab("ai");
    setCcmMessage("");
    setFileRef(null);
    setIsCcmChatStarted(true);
  }, []);


  // GENERAL BOT
  const [generalMessages, setGeneralMessages] = useState([]);
  const [generalMessage, setGeneralMessage] = useState("");
  const [generalSessionId, setGeneralSessionId] = useState("");
  const [isGeneralChatStarted, setIsGeneralChatStarted] = useState(false);
  const [resetTImer, setResetTimer] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // New state for General Copilot Context View
  const [showGeneralContextView, setShowGeneralContextView] = useState(false);

  // AbortController refs for streaming requests
  const rpmStreamControllerRef = useRef(null);
  const ccmStreamControllerRef = useRef(null);
  const generalStreamControllerRef = useRef(null);

  const backPage = sessionStorage.getItem("_back_page");
  const myTasksActiveTab = sessionStorage.getItem("my-tasks/active-tab");

  // SCRUM-687: "Back to Billing" only shows when the chart was reached from
  // the Billing Dashboard - everywhere else keeps the existing back-button
  // behavior/label unchanged.
  const backToBillingUrl =
    backPage === "billing" ? sessionStorage.getItem("_back_url") || "/billings" : null;

  const CCM = Boolean(Number.parseInt(queryParams.get("CCM")));
  const RPM =
    myTasksActiveTab === "Nurse Tasks"
      ? false
      : Boolean(Number.parseInt(queryParams.get("RPM")));

  // just sample data for showing the progress bars 
  const sampleRPM = {
    segments: [
      true,   // device readings
      false,   // two way comm
      true,   // 20 min

    ],
  };

  const sampleCCM = {
    segments: [
      true,   // two way comm
      true,   // care plan created
      false,  // care plan finalized

    ],
  };

  const normalizeCcmChatbot = () => {
    setIsCcmChatStarted(false);
    setCcmMessages([]);
    setDrNoteReceived(false);
    setDrNote(false);
    setCcmSessionId("");
    setCcmUseGenericEndpoint(true);
  };
  const resetCcmEndpointMode = () => {
    setCcmUseGenericEndpoint(false);
  };

  const normalizeBots = () => {
    setSessionId("");
    setIsChatStartedWithReport(false);
    setCcmSessionId("");
    setIsCcmChatStartedWithReport(false);
    setIsChatStarted(false);
    setIsCcmChatStarted(false);
    setMessages([]);
    setMessage("");
    setCcmMessages([]);
    setCcmMessage("");
    setDrNoteReceived(false);
    setDrNote(false);
    sessionStorage.removeItem("streamed_hashes");
  };

  sessionStorage.setItem("ccm", CCM);
  sessionStorage.setItem("rpm", RPM);

  const selectedTab = sessionStorage.getItem("tab-selected");

  const [currentWorkflow, setCurrentWorkflow] = useState(() => {
    return sessionStorage.getItem("_workflow_name") || "All Patients";
  });

  const ids_list = backPage === "my-tasks" ? sessionStorage.getItem("_dp") : sessionStorage.getItem("_mdpl");
  const patientList = ids_list ? JSON.parse(ids_list) : [];

  // Extract patient_id from URL and store in sessionStorage
  useEffect(() => {
    const patientIdFromUrl = queryParams.get("patient_id");
    if (patientIdFromUrl) {
      const storedPatientId = sessionStorage.getItem("_pt_id");
      if (patientIdFromUrl !== storedPatientId) {
        // New patient context: drop the previous patient's captured location
        // so a monitoring save can never use a stale value before the patient
        // record reloads (the capture effect below repopulates it).
        setPatientLocationId(null);
        sessionStorage.setItem("_pt_id", patientIdFromUrl);
        setPatientId(patientIdFromUrl);
      }
    }
  }, [queryParams]);

  useEffect(() => {
    if (position === "Nurse") {
      setIsCareplanExpand(true);
    }
  }, [position]);

  // Sync expand states - only one can be expanded at a time
  useEffect(() => {
    if (isCareplanExpand) {
      setIsCopilotExpand(false);
    }
    if (isCopilotExpand) {
      setIsCareplanExpand(false);
    }
  }, [isCareplanExpand, isCopilotExpand]);

  useEffect(() => {
    if (patientList.length > 0 && patientId) {
      const currentIndex = patientList.indexOf(patientId);
      setCurrentPatientIndex(currentIndex + 1);
      setTotalPatients(patientList.length);
    }
  }, [patientList, patientId]);

  const lastCalledForCurrent = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("_pts_latest_calls");
      const map = raw ? JSON.parse(raw) : {};
      return formatLastCalledHeader(map[patientId]);
    } catch {
      return "--";
    }
  }, [patientId]);

  // Created collapsible section groups to improve UX by reducing excessive scrolling
  const sectionGroups = {
    section_1: ["section_1", "section_2"],
    section_2: ["section_1", "section_2"],

    section_3: ["section_3", "section_4"],
    section_4: ["section_3", "section_4"],

    section_5: ["section_5", "section_6"],
    section_6: ["section_5", "section_6"],
  };

  const toggleSection = (section) => {
    const group = sectionGroups[section];

    setIsSectionOpen((prev) => {
      const shouldOpen = !prev[group[0]];

      const updated = { ...prev };

      group.forEach((sec) => {
        updated[sec] = shouldOpen;
      });

      return updated;
    });
  };

  useEffect(() => {
    setElapsedTimeCCM(0);
    setElapsedTimeRPM(0);
  }, [patientId]);

  // GENERAL BOT
  useEffect(() => {
    const newSessionId = Math.floor(100000000 + Math.random() * 900000000);
    setGeneralSessionId(newSessionId);
  }, []);

  const { data: patientContacts } = useQuery(
    ["patientContacts", patientId],
    () => getPatientContacts(patientId, locationId),
    {
      enabled: !!patientId && !!locationId && !!accountId,
      retry: false,
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        console.error("Error fetching contacts:", error);
        // toast.error("Failed to load contacts");
      },
    }
  );

  // Fetch the current-month care plan at patient page load so the CCM time
  // tracker's careplan gate (sessionStorage `plan_created` + the toggle context
  // `isCurrentMonthCareplanExists`) is correct from the moment the page mounts,
  // not only after the user navigates to the careplan view. The query key
  // matches `careplanNew.js` so react-query reuses the cached response when
  // that view mounts later.
  const today = new Date();
  const currentMonthNumber = today.getMonth() + 1;
  const currentYearNumber = today.getFullYear();
  const { data: currentMonthCarePlan } = useQuery({
    queryKey: [
      "carePlan",
      { month: currentMonthNumber, year: currentYearNumber },
      patientId,
    ],
    queryFn: () => getCarePlan(currentMonthNumber, currentYearNumber),
    enabled: !!patientId && !!locationId && !!accountId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (currentMonthCarePlan === undefined) return;
    const exists = Boolean(currentMonthCarePlan?.data);
    sessionStorage.setItem("plan_created", exists);
    setIsCurrentMonthCareplanExists(exists);
    window.dispatchEvent(new Event("plan_creation_updated"));
  }, [currentMonthCarePlan, setIsCurrentMonthCareplanExists]);

  const createMonitoringSessionMutation = useMutation(
    (sessionData) =>
      // Store the session under the PATIENT's location, not the viewer's
      // currently-selected location (SCRUM-157).
      createMonitoringSession(sessionData, patientId, getPatientLocationId()),
    {
      // Only ever called from handleBackClick, which always wants
      // "dashboard" - passed explicitly rather than read from
      // `pendingNavigation` state, for the same reason as the direct call
      // in handleBackClick (see its comment): state set right before
      // .mutate() isn't guaranteed to be re-read as current by the time
      // this resolves.
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-monitoring-sessions"]);
        // toast.success("Session added successfully");
        handleNoteSaved("dashboard");
      },
      onError: (error) => {
        toast.error("Could not add session");
      },
    }
  );

  const stickyNoteQuery = useQuery(
    ["patientNotes", patientId],
    () => getPatientNotes(patientId),
    {
      enabled: !!patientId,
      staleTime: 2 * 60 * 1000,
      onError: (err) => {
        // toast.error("Failed to load patient notes. Please try again.");
      },
      select: (data) => {
        return data.filter((note) => note.note_type === "sticky_note");
      },
    }
  );

  const {
    data: patient,
    error,
    isLoading,
    refetch,
  } = useQuery(
    ["patient-general-details", patientId],
    () => getPatientGeneralDetails(patientId),
    {
      enabled: !!patientId,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Capture the patient's own location so monitoring-session create/read/update
  // use it instead of the viewer's current location (SCRUM-157).
  useEffect(() => {
    setPatientLocationId(patient?.location?.id);
  }, [patient?.location?.id]);

  const handleNext = () => {
    const currentIndex = patientList.indexOf(patientId);
    if (currentIndex < patientList.length - 1) {
      const nextId = patientList[currentIndex + 1];
      setPendingNavigation(nextId);

      const isThresholdValid = sessionStorage.getItem("mte") === "true";

      if (isThresholdValid && elapsedTimeCCM <= 15 && elapsedTimeRPM <= 15) {
        setPatientId(nextId);
        sessionStorage.setItem("_pt_id", nextId);
        setPendingNavigation(null);
        sessionStorage.removeItem("mte");
      } else if (
        isThresholdValid &&
        (elapsedTimeCCM > 15 || elapsedTimeRPM > 15)
      ) {
        setIsAddNoteModalOpen(true);
      } else {
        setIsAddNoteModalOpen(true);
      }
    }
  };

  const handlePrevious = () => {
    const currentIndex = patientList.indexOf(patientId);
    if (currentIndex > 0) {
      const prevId = patientList[currentIndex - 1];
      setPendingNavigation(prevId);
      const isThresholdValid = sessionStorage.getItem("mte") === "true";

      if (isThresholdValid && elapsedTimeCCM <= 15 && elapsedTimeRPM <= 15) {
        setPatientId(prevId);
        sessionStorage.setItem("_pt_id", prevId);
        setPendingNavigation(null);
        sessionStorage.removeItem("mte");
      } else if (
        isThresholdValid &&
        (elapsedTimeCCM > 15 || elapsedTimeRPM > 15)
      ) {
        setIsAddNoteModalOpen(true);
      } else {
        setIsAddNoteModalOpen(true);
      }
    }
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    setPendingNavigation("dashboard");
    // setIsAddNoteModalOpen(true);
    let data;
    if (monitoringSeconds.ccm_seconds > 0) {
      data = {
        total_ccm_seconds: monitoringSeconds.ccm_seconds,
      };
    }

    if (monitoringSeconds.rpm_seconds > 0) {
      data = {
        ...data,
        total_rpm_seconds: monitoringSeconds.rpm_seconds,
      };
    }

    if (
      monitoringSeconds.ccm_seconds > 0 ||
      monitoringSeconds.rpm_seconds > 0
    ) {
      createMonitoringSessionMutation.mutate(data);
    } else {
      // SCRUM-687: pass "dashboard" explicitly rather than relying on the
      // `pendingNavigation` state set two lines up - setState doesn't apply
      // until the next render, so handleNoteSaved's own
      // `pendingNavigation === "dashboard"` check would still see the old
      // value here and skip the whole "return to Billing" branch below,
      // silently falling through to the generic dashboard redirect (with
      // no restored params) instead.
      handleNoteSaved("dashboard");
    }

    sessionStorage.removeItem("mte");
  };

  // SCRUM-687: `intent`, when passed, overrides the `pendingNavigation`
  // state for callers (handleBackClick, createMonitoringSessionMutation)
  // that set that state and call/resolve into this function within the
  // same tick or an async gap that isn't guaranteed to include a render -
  // reading the state itself there risked seeing the pre-update value. The
  // note-modal's `onNoteSaved={handleNoteSaved}` call (the other caller)
  // passes nothing, correctly falling back to state - by the time a user
  // has interacted with that modal, a render has long since happened.
  const handleNoteSaved = (intent) => {
    const navigationIntent = intent || pendingNavigation;
    if (navigationIntent === "dashboard") {
      const dashType = sessionStorage.getItem("_dash");
      const currentWorkflowTab =
        sessionStorage.getItem("_pt_tab") || "my_all_patients";

      if (backPage === "my-tasks") {
        sessionStorage.removeItem("_back_page")
        return navigate("/my-tasks");
      }

      // SCRUM-687: opened from the Billing Dashboard - return to the exact
      // billing view (month/page/filters) instead of the default dashboard.
      if (backPage === "billing") {
        const billingBackUrl = sessionStorage.getItem("_back_url") || "/billings";
        sessionStorage.removeItem("_back_page");
        sessionStorage.removeItem("_back_url");
        return navigate(billingBackUrl);
      }

      if (dashType === "case_manager") {
        sessionStorage.removeItem("patient_detail_tab");
        navigate(`/dashboard?tab=${currentWorkflowTab}`);
      } else if (dashType === "nurse") {
        navigate("/my-tasks");
      } else {
        navigate(`/dashboard?tab=${currentWorkflowTab}`);
      }
    } else if (pendingNavigation) {
      setPatientId(pendingNavigation);
      sessionStorage.setItem("_pt_id", pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleTrackingTimeRPM = useCallback(({ elapsedTime }) => {
    setMonitoringSeconds((prev) => ({
      ...prev,
      rpm_seconds: elapsedTime,
    }));
  }, []);

  const handleTrackingTimeCCM = useCallback(({ elapsedTime }) => {
    setMonitoringSeconds((prev) => ({
      ...prev,
      ccm_seconds: elapsedTime,
    }));
  }, []);

  useEffect(() => {
    if (patient) {
      setQueryParams((previous) => {
        const params = new URLSearchParams(previous);
        // params.set("CCM", Number(patient.ccm));
        // params.set("RPM", Number(patient.rpm));
        // params.set("patient_id", patientId);
        params.delete("active_tab");
        return params;
      });
      navigate(
        `/patient/details?patient_id=${patientId}&CCM=${+!!patient?.ccm}&RPM=${+!!patient?.rpm}`
      );
    }
  }, [patient]);

  useEffect(() => {
    const fetchSummary = async () => {
      const url = `${BASE_URL_ML}/chatbot/summary?db_schema=${schema_name}&patient_id=${patientId}`;
      const mlFormData = new FormData();

      try {
        setSummaryLoading(true);
        const response = await axios.get(url, mlFormData);
        setSummary(response.data?.summary);
      } catch (error) {
        // handleApiError(error);
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, [patientId]);

  const chatContainerRef = useRef(null);
  const generalChatContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // GENERAL BOT
  const scrollToBottomGeneral = () => {
    if (generalChatContainerRef.current) {
      generalChatContainerRef.current.scrollTo({
        top: generalChatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, ccmMessages]);

  // GENERAL BOT
  useEffect(() => {
    scrollToBottomGeneral();
  }, [generalMessage, generalMessages]);

  const handlePatientType = (monitoringType) => {
    if (CCM && RPM) {
      setPatientType(monitoringType);
    } else if (CCM && !RPM) {
      setPatientType("CCM");
    } else {
      setPatientType("RPM");
    }
  };

  useEffect(() => {
    if (patientType) {
      setPatientType(patientType);
      sessionStorage.setItem("tab-selected", patientType);
    }
  }, [patientType]);

  useEffect(() => {
    if (CCM && RPM) {
      setPatientType("RPM");
    } else if (CCM && !RPM) {
      setPatientType("CCM");
    } else {
      setPatientType("RPM");
    }
    setShowGeneralContextView(false);
  }, [CCM, RPM]);

  useEffect(() => {
    setIsTrackingRPM(true);
    setElapsedTimeCCM(0);
    setElapsedTimeRPM(0);
    setShowGeneralContextView(false);

    // Abort any in-progress streams when patient changes
    if (rpmStreamControllerRef.current) {
      rpmStreamControllerRef.current.abort();
      rpmStreamControllerRef.current = null;
    }
    if (ccmStreamControllerRef.current) {
      ccmStreamControllerRef.current.abort();
      ccmStreamControllerRef.current = null;
    }
    if (generalStreamControllerRef.current) {
      generalStreamControllerRef.current.abort();
      generalStreamControllerRef.current = null;
    }
  }, [patientId]);

  useEffect(() => {
    const fetchFirstRpmQuestion = async () => {
      let url = "";
      if (!sessionId) {
        const newSessionId = Math.floor(100000000 + Math.random() * 900000000);
        setSessionId(newSessionId);

        url = `${BASE_URL_ML}/chatbot/start-chat?db_schema=${schema_name}&patient_id=${patientId}&patient_type=RPM&session_id=${newSessionId}`;
      } else {
        url = `${BASE_URL_ML}/chatbot/start-chat?db_schema=${schema_name}&patient_id=${patientId}&patient_type=RPM&session_id=${sessionId}`;
      }

      const mlFormData = new FormData();
      if (fileRef) mlFormData.append("file_ref", fileRef);

      // Abort any existing RPM stream
      if (rpmStreamControllerRef.current) {
        rpmStreamControllerRef.current.abort();
      }
      const { controller, clear } = createStreamController();
      rpmStreamControllerRef.current = controller;

      try {
        setMessages([{ type: "bot", loading: true }]);

        await streamingFetch({
          url,
          body: mlFormData,
          controller,
          onChunk: (accumulatedText) => {
            setMessages([{ type: "bot", text: accumulatedText, streaming: true }]);
          },
          onComplete: (finalText) => {
            clear();
            rpmStreamControllerRef.current = null;
            // Mark as streamed (not streaming) so it renders directly without re-animation
            setMessages([{ type: "bot", text: finalText, streamed: true }]);
          },
          onError: (error) => {
            clear();
            rpmStreamControllerRef.current = null;
            handleApiError(error);
          },
        });
      } catch (error) {
        clear();
        rpmStreamControllerRef.current = null;
        handleApiError(error);
      }
    };

    const fetchFirstCcmQuestion = async () => {
      let url = "";
      const mlFormData = new FormData();

      let newSessionId;
      if (!ccmSessionId) {
        newSessionId = Math.floor(100000000 + Math.random() * 900000000);
        setCcmSessionId(newSessionId);
        sessionStorage.setItem("ccm_session_id", newSessionId);
      }
      if (fileRef) mlFormData.append("file_ref", fileRef);

      if (isCurrentMonthCareplanExists) {
        url = `${BASE_URL_ML}/chatbot/update-ccm-careplan?db_schema=${schema_name}&patient_id=${patientId}&patient_type=CCM&session_id=${ccmSessionId || newSessionId
          }`;

        setCareplanMode("edit");
      } else {
        url = `${BASE_URL_ML}/chatbot/ccm/start?db_schema=${schema_name}&patient_id=${patientId}&patient_type=CCM&session_id=${ccmSessionId || newSessionId
          }`;
        setCareplanMode("new");
      }

      // Abort any existing CCM stream
      if (ccmStreamControllerRef.current) {
        ccmStreamControllerRef.current.abort();
      }
      const { controller, clear } = createStreamController();
      ccmStreamControllerRef.current = controller;

      try {
        setCcmMessages([{ type: "bot", loading: true }]);

        await streamingFetch({
          url,
          body: mlFormData,
          controller,
          onChunk: (accumulatedText) => {
            setCcmMessages([{ type: "bot", text: accumulatedText, streaming: true }]);
          },
          onComplete: (finalText) => {
            clear();
            ccmStreamControllerRef.current = null;
            // Mark as streamed (not streaming) so it renders directly without re-animation
            setCcmMessages([{ type: "bot", text: finalText, streamed: true }]);
          },
          onError: (error) => {
            clear();
            ccmStreamControllerRef.current = null;
            handleApiError(error);
          },
        });
      } catch (error) {
        clear();
        ccmStreamControllerRef.current = null;
        handleApiError(error);
      }
    };

    if (isChatStarted && chatBotType === "RPM") {
      fetchFirstRpmQuestion();
    }

    if (isCcmChatStarted && chatBotType === "CCM") {
      fetchFirstCcmQuestion();
    }
  }, [isChatStarted, isCcmChatStarted]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (chatBotType === "RPM") {
      const trimmedMsg = message.trim();
      const activeFileRef = fileRef;
      if (trimmedMsg === "" && !activeFileRef) return;

      setMessages((prev) => [
        ...prev,
        ...(trimmedMsg ? [{ type: "user", text: trimmedMsg }] : []),
        { type: "bot", loading: true },
      ]);
      setMessage("");

      // Abort any existing RPM stream
      if (rpmStreamControllerRef.current) {
        rpmStreamControllerRef.current.abort();
      }
      const { controller, clear } = createStreamController();
      rpmStreamControllerRef.current = controller;

      if (activeFileRef) {
        setFileRef(null);
        window.dispatchEvent(new Event("clear_chat_uploaded_documents"));
      }

      try {
        await streamingFetch({
          url: `${BASE_URL_ML}/chatbot/chat`,
          body: {
            patient_type: chatBotType,
            user_input: trimmedMsg,
            session_id: String(sessionId),
            patient_id: patientId,
            ...(activeFileRef ? { file_ref: activeFileRef } : {}),
          },
          controller,
          onChunk: (accumulatedText) => {
            setMessages((prev) => {
              const updated = [...prev];
              const loadingIndex = updated.findIndex((msg) => msg.loading || msg.streaming);
              if (loadingIndex !== -1) {
                updated[loadingIndex] = { type: "bot", text: accumulatedText, streaming: true };
              }
              return updated;
            });
          },
          onComplete: (finalText) => {
            clear();
            rpmStreamControllerRef.current = null;
            // Mark as streamed (not streaming) so it renders directly without re-animation
            setMessages((prev) => {
              const updated = [...prev];
              const loadingIndex = updated.findIndex((msg) => msg.loading || msg.streaming);
              if (loadingIndex !== -1) {
                updated[loadingIndex] = { type: "bot", text: finalText, streamed: true };
              }
              return updated;
            });
          },
          onError: (error) => {
            clear();
            rpmStreamControllerRef.current = null;
            handleApiError(error);
          },
        });
      } catch (error) {
        clear();
        rpmStreamControllerRef.current = null;
        handleApiError(error);
      }
    } else if (chatBotType === "CCM") {
      const trimmedMsg = ccmMessage.trim();
      const activeFileRef = fileRef;
      if (trimmedMsg === "" && !activeFileRef) return;

      setCcmMessages((prev) => [
        ...prev,
        ...(trimmedMsg ? [{ type: "user", text: trimmedMsg }] : []),
        { type: "bot", loading: true },
      ]);
      setCcmMessage("");

      // Abort any existing CCM stream
      if (ccmStreamControllerRef.current) {
        ccmStreamControllerRef.current.abort();
      }
      const { controller, clear } = createStreamController();
      ccmStreamControllerRef.current = controller;

      if (activeFileRef) {
        setFileRef(null);
        window.dispatchEvent(new Event("clear_chat_uploaded_documents"));
      }

      try {
        await streamingFetch({
          // Endpoint-only switch — see ccmUseGenericEndpoint declaration.
          url: `${BASE_URL_ML}/${ccmUseGenericEndpoint ? "chatbot/chat" : "chatbot/ccm/chat"
            }`,
          body: {
            patient_type: chatBotType,
            user_input: trimmedMsg,
            session_id: String(ccmSessionId),
            patient_id: patientId,
            ...(activeFileRef ? { file_ref: activeFileRef } : {}),
          },
          controller,
          onChunk: (accumulatedText) => {
            setCcmMessages((prev) => {
              const updated = [...prev];
              const loadingIndex = updated.findIndex((msg) => msg.loading || msg.streaming);
              if (loadingIndex !== -1) {
                updated[loadingIndex] = { type: "bot", text: accumulatedText, streaming: true };
              }
              return updated;
            });
          },
          onComplete: (finalText) => {
            clear();
            ccmStreamControllerRef.current = null;
            // Mark as streamed (not streaming) so it renders directly without re-animation
            setCcmMessages((prev) => {
              const updated = [...prev];
              const loadingIndex = updated.findIndex((msg) => msg.loading || msg.streaming);
              if (loadingIndex !== -1) {
                updated[loadingIndex] = { type: "bot", text: finalText, streamed: true };
              }
              return updated;
            });
          },
          onError: (error) => {
            clear();
            ccmStreamControllerRef.current = null;
            handleApiError(error);
          },
        });
      } catch (error) {
        clear();
        ccmStreamControllerRef.current = null;
        handleApiError(error);
      }
    } else if (chatBotType === "General") {
      const trimmedMsg = generalMessage.trim();
      if (trimmedMsg === "") return;

      setGeneralMessages((prev) => [
        ...prev,
        { type: "user", text: trimmedMsg },
        { type: "bot", loading: true },
      ]);
      setGeneralMessage("");

      try {
        const response = await axios.post(
          `${BASE_URL_ML}/general-purpose-chatbot/general_purpose_chatbot/`,
          {},
          {
            params: {
              query: trimmedMsg,
              session_id: String(generalSessionId),
              db_schema: schema_name,
              patient_id: patientId,
            },
          }
        );

        const botReply = response.data;

        setGeneralMessages((prev) => {
          const updated = [...prev];
          const loadingIndex = updated.findIndex((msg) => msg.loading);
          if (loadingIndex !== -1) {
            updated[loadingIndex] = { type: "bot", text: botReply };
          }
          return updated;
        });
      } catch (error) {
        handleApiError(error);
      }
    }
  };

  // ------- CCM quick actions (Condition Guidelines / Red-Flag Warning Signs) -------
  const CCM_QUICK_ACTIONS = {
    "condition-guidelines": { label: "Condition Guidelines" },
    "warning-signs": { label: "Red-Flag Warning Signs" },
  };

  const handleCcmQuickAction = async (action) => {
    const quickAction = CCM_QUICK_ACTIONS[action];
    if (!quickAction) return;

    if (!ccmSessionId) {
      toast.error("No active chat session. Start a chat first.");
      return;
    }
    // Secondary guard; the buttons are also disabled while one is in flight.
    if (ccmQuickAction) return;

    setCcmQuickAction(action);
    setCcmMessages((prev) => [
      ...prev,
      { type: "user", text: quickAction.label, quickAction: action },
      { type: "bot", loading: true },
    ]);

    const settlePendingBubble = (text, tagAsQuickAction = false) => {
      setCcmMessages((prev) => {
        const updated = [...prev];
        const pendingIndex = updated.findIndex((msg) => msg.loading || msg.streaming);
        if (pendingIndex !== -1) {
          updated[pendingIndex] = {
            type: "bot",
            text,
            streamed: true,
            ...(tagAsQuickAction ? { quickAction: action } : {}),
          };
        }
        return updated;
      });
    };

    try {
      const response = await axios.post(`${BASE_URL_ML}/chatbot/ccm/${action}`, {
        session_id: String(ccmSessionId),
      });
      const markdown = response.data?.markdown?.trim();
      settlePendingBubble(
        markdown || `No ${quickAction.label.toLowerCase()} are available for this patient yet.`,
        true
      );
    } catch (error) {
      // Leaving the loading bubble would keep the input and quick actions
      // disabled, so settle it with a readable failure line.
      settlePendingBubble(`Couldn't load ${quickAction.label}. Please try again.`);
      handleApiError(error);
    } finally {
      setCcmQuickAction(null);
    }
  };

  const handleGeneralBotSubmit = async (e) => {
    e.preventDefault();
    const trimmedMsg = generalMessage.trim();
    if (trimmedMsg === "") return;

    setGeneralMessages((prev) => [
      ...prev,
      { type: "user", text: trimmedMsg },
      { type: "bot", loading: true },
    ]);
    setGeneralMessage("");

    // Abort any existing General stream
    if (generalStreamControllerRef.current) {
      generalStreamControllerRef.current.abort();
    }
    const { controller, clear } = createStreamController();
    generalStreamControllerRef.current = controller;

    try {
      const queryParams = new URLSearchParams({
        db_schema: schema_name,
        session_id: String(generalSessionId),
        patient_id: patientId,
        query: trimmedMsg,
        include_history_summary: "true",
      });

      await streamingFetch({
        url: `${BASE_URL_ML}/general-purpose-chatbot/chatbot?${queryParams.toString()}`,
        body: new FormData(),
        controller,
        onChunk: (accumulatedText) => {
          setGeneralMessages((prev) => {
            const updated = [...prev];
            const loadingIndex = updated.findIndex((msg) => msg.loading || msg.streaming);
            if (loadingIndex !== -1) {
              updated[loadingIndex] = { type: "bot", text: accumulatedText, streaming: true };
            }
            return updated;
          });
        },
        onComplete: (finalText) => {
          clear();
          generalStreamControllerRef.current = null;
          setGeneralMessages((prev) => {
            const updated = [...prev];
            const loadingIndex = updated.findIndex((msg) => msg.loading || msg.streaming);
            if (loadingIndex !== -1) {
              updated[loadingIndex] = { type: "bot", text: finalText, streamed: true };
            }
            return updated;
          });
        },
        onError: (error) => {
          clear();
          generalStreamControllerRef.current = null;
          handleApiError(error);
        },
      });
    } catch (error) {
      clear();
      generalStreamControllerRef.current = null;
      handleApiError(error);
    }
  };

  const user = patient?.user;

  useEffect(() => {
    if (activeTab === "devices" && !RPM) {
      setActiveTab("details");
    }
  }, [RPM, CCM, activeTab]);

  useEffect(() => {
    sessionStorage.setItem("patient_detail_tab", activeTab);
  }, [activeTab]);

  //  GENERAL BOT
  useEffect(() => {
    if (generalMessages.length <= 0) {
      setGeneralMessages([
        { type: "bot", text: "Hi, How can i help you?" },
      ]);
    }
  }, []);

  if (isLoading) return <Spinner />;
  if (error)
    return <Reload message="Could not load patient" onReload={refetch} />;

  return (
    <div className={styles.patient_page}>
      <>
        <div className={styles.name_container_spacer}></div>

        <div className={styles.name_container}>
          <div className={styles.name_wrapper}>
            <Link
              to={backToBillingUrl || "/dashboard"}
              className={styles.back_btn}
              onClick={handleBackClick}
              aria-label={backToBillingUrl ? "Back to Billing" : "Back to dashboard"}
              title={backToBillingUrl ? "Back to Billing" : "Back to dashboard"}
            >
              <FontAwesomeIcon icon={faArrowLeftLong} />
            </Link>

            {isLoading === false && (
              <div className={styles.name_and_status}>

                {/* NAME + INDICATOR */}
                <div className={styles.patient_header_info}>

                  <div className={styles.nameWithIndicatorInline}>

                    <h3>
                      {`${user?.first_name} ${user?.last_name}`}
                    </h3>

                    {/* Indicator on RIGHT side of name */}
                    <BillingProgressGroup
                      rpm={patient?.rpm_ccm_indicator?.rpm_indicator}
                      ccm={patient?.rpm_ccm_indicator?.ccm_indicator}
                      isLight={true}
                      isPatientDetail={true}
                    />

                  </div>

                  {currentWorkflow !== "All Patients" && (
                    <span className={styles.workflow_tag}>
                      {currentWorkflow}
                    </span>
                  )}
                </div>

                {/* Sticky Notes — inline placeholder when empty (TH-B6 makes it editable) */}
                <div className={styles.sticky_notes_container}>
                  {stickyNoteQuery?.data?.length === 0 ? (
                    <p className={styles.sticky_note_placeholder}>
                      Sticky Notes can go here
                    </p>
                  ) : (
                    stickyNoteQuery?.data?.map((note) => (
                      <p key={note.id} className={styles.sticky_note}>
                        {note.note_text}
                      </p>
                    ))
                  )}
                </div>

              </div>
            )}
          </div>


          <div className={styles.timer_and_history}>
            {selectedTab === "CCM" ? (
              <TimeTracker
                key="ccm-timer"
                id="ccm-timer"
                headerStyle={true}
              initialIsTracking={true}
                onStateChange={handleTrackingTimeCCM}
                selectedTab="CCM"
                elapsedTime={elapsedTimeCCM}
                setElapsedTime={setElapsedTimeCCM}
                isTracking={isTrackingCCM}
                setIsTracking={setIsTrackingCCM}
                resetTimer={resetTImer}
                setResetTimer={setResetTimer}
              />
            ) : (
              <TimeTracker
                key="rpm-timer"
                id="rpm-timer"
                headerStyle={true}
              initialIsTracking={true}
                onStateChange={handleTrackingTimeRPM}
                selectedTab="RPM"
                elapsedTime={elapsedTimeRPM}
                setElapsedTime={setElapsedTimeRPM}
                isTracking={isTrackingRPM}
                setIsTracking={setIsTrackingRPM}
                resetTimer={resetTImer}
                setResetTimer={setResetTimer}
              />
            )}

            {/* Reset timer to 00:00:00 (clears elapsed via setResetTimer flag in TimeTracker) */}
            <button
              type="button"
              className={styles.session_history_btn}
              aria-label="Reset timer"
              title="Reset timer"
              onClick={() => setResetTimer(true)}
            >
              <FaHistory size={14} />
            </button>
          </div>
        </div>

        {/* Page tabs row — sits in the gray bg ABOVE the white card per Figma
            node 595:1307. Lifted out of `.patient_page_container` (the white
            outer card) so it floats on the gray strip between the dark navy
            sub-header and the white card. Counter and prev/next buttons stay
            hidden via .counter_tab / .navigation_buttons display:none. */}
        <div className={styles.tabs_container}>
          {activeTab !== "details" && (
            <div
              className={`${styles.navigation_buttons} ${patientList.indexOf(patientId) === 0 ? styles.btn_disabled : ""
                }`}
              onClick={
                isLoading
                  ? null
                  : patientList.indexOf(patientId) === 0
                    ? null
                    : handlePrevious
              }
              disabled={patientList.indexOf(patientId) === 0}
            >
              <FontAwesomeIcon icon={faArrowLeftLong} />
            </div>
          )}

          <div className={styles.tabs_toggler}>
            <div
              className={`${styles.tab} ${activeTab === "details" ? styles.active : ""
                }`}
              onClick={() => setActiveTab("details")}
            >
              Details
            </div>
            <div
              className={`${styles.tab} ${activeTab === "settings" ? styles.active : ""
                }`}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </div>
            <div
              className={`${styles.tab} ${activeTab === "time" ? styles.active : ""
                }`}
              onClick={() => setActiveTab("time")}
            >
              Monitoring Time
            </div>
            {RPM && (
              <div
                className={`${styles.tab} ${activeTab === "devices" ? styles.active : ""
                  }`}
                onClick={() => setActiveTab("devices")}
              >
                Devices
              </div>
            )}
            <div
              className={`${styles.tab} ${activeTab === "docs" ? styles.active : ""
                }`}
              onClick={() => setActiveTab("docs")}
            >
              Patient Documents
            </div>
            <div
              className={`${styles.tab} ${activeTab === "videos" ? styles.active : ""
                }`}
              onClick={() => setActiveTab("videos")}
            >
              Instructional Videos
            </div>
          </div>
          {activeTab !== "details" && (
            <div className={styles.counter_tab}>
              Patient {currentPatientIndex} / {totalPatients}
            </div>
          )}

          {activeTab !== "details" && (
            <div
              className={`${styles.navigation_buttons} ${patientList.indexOf(patientId) === patientList.length - 1
                ? styles.btn_disabled
                : ""
                }`}
              onClick={
                isLoading
                  ? null
                  : patientList.indexOf(patientId) === patientList.length
                    ? null
                    : () => handleNext()
              }
              disabled={
                patientList.indexOf(patientId) === patientList.length - 1
              }
            >
              <FontAwesomeIcon icon={faArrowRightLong} />
            </div>
          )}
        </div>

        <div className={styles.patient_page_container}>
          {isLoading && (
            <div className={styles.spinner_container}>
              <Spinner />
            </div>
          )}

          {isLoading === false && (
            <>
              {activeTab === "details" && (
                <DetailsTab
                  chatBotType={chatBotType}
                  setChatBotType={setChatBotType}
                  rpmEnabled={RPM}
                  ccmEnabled={CCM}
                  patientContacts={patientContacts}
                  summary={summary}
                  summaryLoading={summaryLoading}
                  chartsIsOpen={isSectionOpen.section_3}
                  chartsOnClose={() => toggleSection("section_3")}
                  handlePatientType={handlePatientType}
                  setChatBotType={setChatBotType}
                  isCcmChatStarted={isCcmChatStarted}
                  onStartTocaCopilot={handleStartTocaCopilot}
                  patientType={patientType}
                  sessionId={sessionId}
                  ccmSessionId={ccmSessionId}
                  resetTimer={setResetTimer}
                  monitoringIsOpen={isSectionOpen.section_6}
                  monitoringOnClose={() => toggleSection("section_6")}
                  defaultTimer={{ elapsedTimeCCM, elapsedTimeRPM }}
                  noteToPaste={noteToPaste}
                  hasChatbotAccess={hasChatbotAccess}
                  onNoteClick={(text) =>
                    setNoteToPaste({ text, timestamp: Date.now() })
                  }
                  notesIsOpen={isSectionOpen.section_5}
                  notesOnClose={() => toggleSection("section_5")}
                  notesAiTab={notesAiTab}
                  setNotesAiTab={setNotesAiTab}
                  chatProps={{
                    chatBotType,
                    isChatStarted,
                    setIsChatStarted,
                    isCcmChatStarted,
                    setIsCcmChatStarted,
                    messages,
                    message,
                    setMessage,
                    ccmMessages,
                    ccmMessage,
                    setCcmMessage,
                    chatContainerRef,
                    setDrNoteReceived,
                    setDrNote,
                    handleSubmit,
                    handleCcmQuickAction,
                    ccmQuickAction,
                    normalizeCcmChatbot,
                    resetCcmEndpointMode,
                    setSessionId,
                    setIsChatStartedWithReport,
                    setCcmSessionId,
                    setIsCcmChatStartedWithReport,
                    showUploadModal,
                    setShowUploadModal,
                    sessionId,
                    ccmSessionId,
                    fileRef,
                    setFileRef,
                    onNotesGenerated: (text) =>
                      setNoteToPaste({ text, timestamp: Date.now() }),
                    clearMessagesForActiveTab: () => {
                      if (chatBotType === "CCM") {
                        setCcmMessages([]);
                        setCcmSessionId("");
                      } else {
                        setMessages([]);
                        setSessionId("");
                      }
                      setFileRef(null);
                    },
                  }}
                  lastCalled={lastCalledForCurrent}
                  currentPatientIndex={currentPatientIndex}
                  totalPatients={totalPatients}
                  canGoBack={patientList.indexOf(patientId) > 0}
                  canGoForward={
                    patientList.indexOf(patientId) <
                    patientList.length - 1
                  }
                  onBackPatient={handlePrevious}
                  onForwardPatient={handleNext}
                />
              )}

              {activeTab === "settings" && <PatientSettings />}

              {activeTab === "time" && <MonitoringTime />}

              {activeTab === "devices" && RPM && (
                <PatientDevices currentPatient={patient} />
              )}

              {activeTab === "docs" && <PatientDocuments />}

              {activeTab === "videos" && (
                <section className={styles.patient_page_section}>
                  <InstructionalVideos currentPatient={patient} />
                </section>
              )}
            </>
          )}
        </div>
      </>
      <AddNoteDialogue
        isOpen={isAddNoteModalOpen}
        onClose={() => {
          setIsAddNoteModalOpen(false);
          setPendingNavigation(null);
        }}
        note={currentNote}
        showNoteTypeSelector={true}
        onNoteSaved={handleNoteSaved}
        monitoringSeconds={monitoringSeconds}
        normalizeBots={normalizeBots}
      />
    </div>
  );
};

export default PatientDetail;